document.documentElement.classList.add('lc-js');

(() => {
  const formatMoney = (cents, currency) => {
    const value = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-CA', {
        style: 'currency',
        currency: currency || 'CAD'
      }).format(value);
    } catch (_error) {
      return '$' + value.toFixed(2);
    }
  };

  const initializeMenu = (root = document) => {
    root.querySelectorAll('[data-lc-menu-toggle]').forEach((button) => {
      if (button.dataset.initialized === 'true') return;
      button.dataset.initialized = 'true';
      const navId = button.getAttribute('aria-controls');
      const nav = document.getElementById(navId);
      if (!nav) return;

      const closeMenu = () => {
        button.setAttribute('aria-expanded', 'false');
        nav.classList.remove('is-open');
        document.body.classList.remove('lc-menu-open');
      };

      button.addEventListener('click', () => {
        const isOpen = button.getAttribute('aria-expanded') === 'true';
        button.setAttribute('aria-expanded', String(!isOpen));
        nav.classList.toggle('is-open', !isOpen);
        document.body.classList.toggle('lc-menu-open', !isOpen);
      });

      nav.querySelectorAll('a').forEach((link) => link.addEventListener('click', closeMenu));
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') closeMenu();
      });
    });
  };

  const initializeReveal = (root = document) => {
    const items = [...root.querySelectorAll('.lc-reveal:not([data-lc-reveal-ready])')];
    if (!items.length) return;
    items.forEach((item) => item.setAttribute('data-lc-reveal-ready', 'true'));

    if (!('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );
    items.forEach((item) => observer.observe(item));
  };

  const initializeVariantSelectors = (root = document) => {
    root.querySelectorAll('[data-lc-product-form]').forEach((form) => {
      if (form.dataset.initialized === 'true') return;
      form.dataset.initialized = 'true';
      const select = form.querySelector('[data-lc-variant-select]');
      if (!select) return;
      const scope = form.closest('[data-lc-product-scope]') || document;

      const update = () => {
        const option = select.options[select.selectedIndex];
        const available = option.dataset.available === 'true';
        const price = option.dataset.price;
        const compareAt = option.dataset.compareAt;
        const currency = option.dataset.currency || 'CAD';
        scope.querySelectorAll('[data-lc-price]').forEach((element) => {
          element.textContent = formatMoney(price, currency);
        });
        scope.querySelectorAll('[data-lc-compare-price]').forEach((element) => {
          const showCompare = Number(compareAt) > Number(price);
          element.textContent = showCompare ? formatMoney(compareAt, currency) : '';
          element.hidden = !showCompare;
        });
        scope.querySelectorAll('[data-lc-add-button]').forEach((button) => {
          button.disabled = !available;
          const availableText = button.dataset.availableText;
          const soldOutText = button.dataset.soldOutText;
          button.textContent = available ? availableText : soldOutText;
        });
      };

      select.addEventListener('change', update);
      update();
    });
  };

  const initializeProductForms = (root = document) => {
    root.querySelectorAll('[data-lc-product-form]').forEach((form) => {
      if (form.dataset.lcSubmitInitialized === 'true') return;
      form.dataset.lcSubmitInitialized = 'true';

      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        const formId = form.getAttribute('id');
        const buttons = document.querySelectorAll(`[form="${formId}"][data-lc-add-button], #${formId} [data-lc-add-button]`);
        const status = form.querySelector('[data-lc-form-status]');
        const routeRoot = window.Shopify?.routes?.root || '/';

        buttons.forEach((button) => {
          button.disabled = true;
          button.dataset.originalText = button.textContent;
          button.textContent = button.dataset.loadingText || 'Adding…';
        });
        if (status) status.textContent = '';

        try {
          const response = await fetch(routeRoot + 'cart/add.js', {
            method: 'POST',
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: new FormData(form)
          });
          if (!response.ok) throw new Error('Cart request failed');
          window.location.assign(routeRoot + 'cart');
        } catch (_error) {
          buttons.forEach((button) => {
            button.disabled = false;
            button.textContent = button.dataset.originalText || button.dataset.availableText || 'Add to cart';
          });
          if (status) status.textContent = 'We could not add that selection. Please try again.';
        }
      });
    });
  };

  const initializeGallery = (root = document) => {
    root.querySelectorAll('[data-lc-gallery]').forEach((gallery) => {
      if (gallery.dataset.initialized === 'true') return;
      gallery.dataset.initialized = 'true';
      const mainImage = gallery.querySelector('[data-lc-gallery-main]');
      if (!mainImage) return;

      gallery.querySelectorAll('[data-lc-gallery-thumb]').forEach((button) => {
        button.addEventListener('click', () => {
          const source = button.dataset.src;
          if (!source) return;
          mainImage.src = source;
          mainImage.removeAttribute('srcset');
          mainImage.alt = button.dataset.alt || '';
          gallery.querySelectorAll('[data-lc-gallery-thumb]').forEach((thumb) => {
            thumb.classList.toggle('is-active', thumb === button);
            thumb.setAttribute('aria-pressed', String(thumb === button));
          });
        });
      });
    });
  };

  const initializeBundles = (root = document) => {
    root.querySelectorAll('[data-lc-bundle-add]').forEach((button) => {
      if (button.dataset.initialized === 'true') return;
      button.dataset.initialized = 'true';
      button.addEventListener('click', async () => {
        const status = button.closest('.lc-bundle')?.querySelector('[data-lc-bundle-status]');
        let items;
        try {
          items = JSON.parse(button.dataset.items || '[]');
        } catch (_error) {
          items = [];
        }
        if (!items.length) return;

        const originalText = button.textContent;
        button.disabled = true;
        button.textContent = button.dataset.loadingText || originalText;
        if (status) status.textContent = '';

        try {
          const routeRoot = window.Shopify?.routes?.root || '/';
          const response = await fetch(routeRoot + 'cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            },
            body: JSON.stringify({ items })
          });
          if (!response.ok) throw new Error('Cart request failed');
          window.location.assign(button.dataset.cartUrl || '/cart');
        } catch (_error) {
          button.disabled = false;
          button.textContent = originalText;
          if (status) status.textContent = button.dataset.errorText || '';
        }
      });
    });
  };

  const initialize = (root = document) => {
    initializeMenu(root);
    initializeReveal(root);
    initializeVariantSelectors(root);
    initializeProductForms(root);
    initializeGallery(root);
    initializeBundles(root);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
